import Notification from '@rio-cloud/rio-uikit/Notification';
import React, { useEffect, useState } from 'react';
import { getVariablesAll, saveChallenge, updateChallenge } from '../../../../services/challenges';
import { EChallengesType, ECriterion, EPillar, IChallenges } from '../../../../types/challenges';
import EditForm from './EditForm';
import EditHeader from './EditHeader';

const criterionOptions = [
  { label: 'Selecione...', value: '' },
  { label: 'Menor ou Igual', value: ECriterion.LESS_EQUAL },
  { label: 'Maior ou Igual', value: ECriterion.GREATER_EQUAL },
  { label: 'Igual', value: ECriterion.EQUAL },
  { label: 'Diferente', value: ECriterion.DIFFERENT },
  { label: 'Entre', value: ECriterion.BETWEEN },
];

const typeOptions = [
  { label: 'Selecione...', value: '' },
  { label: 'Personalizado', value: EChallengesType.PERSONALIZED },
  { label: 'Padrão', value: EChallengesType.DEFAULT },
];

/** Active allowed only when validUntil is strictly after today (date-only, local). */
function isValidUntilAfterToday(validUntil: string | null | undefined): boolean {
  if (!validUntil) return false;
  const raw = /^\d{4}-\d{2}-\d{2}$/.test(validUntil)
    ? validUntil
    : (() => {
        const d = new Date(validUntil);
        if (isNaN(d.getTime())) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      })();
  if (!raw) return false;

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return raw > todayKey;
}

interface EditProps {
  onBack: () => void;
  mode: 'create' | 'edit';
  challenge?: IChallenges;
}

const initialForm = {
  name: '',
  description: '',
  criterion: '' as ECriterion | '',
  criterionValue: 0,
  criterionSecondValue: 0,
  // Pilar oculto no protótipo; API ainda exige o campo.
  pillar: EPillar.SAFE as EPillar | '',
  variableId: 1,
  points: 0,
  uniqueExecute: false,
  active: true,
  validUntil: '',
  type: '' as EChallengesType | '',
};

const Edit: React.FC<EditProps> = ({ onBack, mode, challenge }) => {
  const [form, setForm] = useState(initialForm);
  const [optionsVariables, setOptionsVariables] = useState<any[]>([]);

  const handleFormChange = (field: keyof typeof initialForm, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const variablesData = await getVariablesAll();
        setOptionsVariables(variablesData);
      } catch (error) {
        console.error('Erro ao buscar variáveis:', error);
        Notification.error('Não foi possível carregar as variáveis.');
        setOptionsVariables([]);
      }
    };

    fetchVariables();
  }, []);


  useEffect(() => {
    if (mode === 'edit' && challenge) {
      const validUntil = challenge.validUntil || '';
      const canBeActive = isValidUntilAfterToday(validUntil);
      setForm({
        name: challenge.name || '',
        description: challenge.description ?? '',
        criterion: challenge.criterion || '',
        criterionValue: challenge.criterionValue ?? 0,
        criterionSecondValue: challenge.criterionSecondValue ?? 0,
        pillar: challenge.pillar || EPillar.SAFE,
        variableId: challenge.variableId || 0,
        points: challenge.points ?? 0,
        // Do NOT use `||` here: `false || true` always becomes true for inactive challenges.
        uniqueExecute: Boolean(challenge.uniqueExecute),
        active: canBeActive ? Boolean(challenge.active) : false,
        validUntil,
        type: challenge.type || '',
      });
    } else if (mode === 'create') {
      setForm(initialForm);
    }
  }, [mode, challenge]);

  const handleSubmit = async () => {

    if (!form.criterion) {
      Notification.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (form.variableId <= 0) {
      Notification.error('Escolha uma variável válida antes de salvar.');
      return;
    }

    const canBeActive = isValidUntilAfterToday(form.validUntil);
    const activeToSave = canBeActive ? form.active : false;

    const payloadBase: Omit<IChallenges, 'id'> = {
      name: form.name,
      description: form.description,
      criterion: form.criterion as ECriterion,
      criterionValue: form.criterionValue,
      criterionSecondValue: form.criterionSecondValue,
      pillar: (form.pillar || EPillar.SAFE) as EPillar,
      variableId: form.variableId,
      points: form.points,
      uniqueExecute: form.uniqueExecute,
      active: activeToSave,
      validUntil: form.validUntil,
      type: EChallengesType.PERSONALIZED,
    };

    try {
      if (mode === 'create') {
        await saveChallenge(payloadBase);
        Notification.success('Desafio criado com sucesso!');
      } else if (mode === 'edit' && challenge) {
        const payloadWithId: IChallenges = {
          id: challenge.id,
          ...payloadBase,
        };
        await updateChallenge(challenge.id.toString(), payloadWithId);
        Notification.success('Desafio atualizado com sucesso!');
      }

      onBack();
    } catch (err: any) {
      const backendMessage = err?.response?.data?.message;
      const message =
        backendMessage && typeof backendMessage === 'object'
          ? Object.values(backendMessage).join(' ')
          : backendMessage;
      Notification.error(message || err?.message || 'Erro ao salvar desafio.');
    }
  };

  return (
    <div className="min-h-screen text-color-info ">
      <div className="width-70pct margin-x-auto padding-50">
        <div className="margin-top-40 margin-bottom-32 padding-y-10">
          <EditHeader mode={mode} />
        </div>
        <div className="panel panel-default panel-body shadow-default rounded text-color-info">
          <EditForm
            form={form}
            onFormChange={handleFormChange}
            criterionOptions={criterionOptions}
            variableOptions={optionsVariables}
            onBack={onBack}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default Edit; 